import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnDestroy, Optional } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ValidationRulesService, ValidationRulesMap, RuleSet } from './validation-rules.service';
import { evaluateCondition } from './validation-condition-evaluator';
import { buildConditions } from './validation-derivations';
import { runRule, ClientValidationContext } from './validation-client-validators';
import { ValidationErrorsRegistry, FieldErrors } from './validation-errors-registry.service';
import { CUSTOM_VALIDATORS } from './validation-custom-validators';

interface BoundField {
    el: HTMLElement;
    property: string;
    nameAttr: string | null;
    isPEditor: boolean;
    appliedAttrs: Set<string>;
    quillCleanup?: () => void;
}

@Directive({
    selector: '[validationEntity], [validation-entity]',
    standalone: true,
    exportAs: 'validationEntity'
})
export class ValidationEntityDirective implements AfterViewInit, OnDestroy {
    @Input() validationEntity!: string;
    @Input() validationMode = 2;
    @Input() liveValidation = true;

    private rulesByMode = new Map<number, ValidationRulesMap>();
    private rulesMap: ValidationRulesMap = {};
    private boundFields: BoundField[] = [];
    private subscription?: Subscription;
    private zoneStableSub?: Subscription;
    private lastZoneSnapshot: Record<string, string> = {};
    private lastCandidateCount = 0;
    private interactedFields = new Set<string>();

    constructor(
        private host: ElementRef<HTMLElement>,
        private rulesService: ValidationRulesService,
        private zone: NgZone,
        private errorsRegistry: ValidationErrorsRegistry,
        @Optional() private ngForm?: NgForm,
    ) {
        console.log('[validationEntity] CONSTRUCTED on element:', host.nativeElement.tagName,
            'entity=', host.nativeElement.getAttribute('validationEntity') || host.nativeElement.getAttribute('validation-entity'));
    }

    private reapplyTimer: any = null;
    private domChangeHandler = () => this.scheduleReapply();

    async ngAfterViewInit(): Promise<void> {
        if (!this.validationEntity) return;

        const cached1 = this.rulesService.getRulesSync(this.validationEntity, 1);
        const cached2 = this.rulesService.getRulesSync(this.validationEntity, 2);
        if (cached1 || cached2) {
            if (cached1) this.rulesByMode.set(1, cached1);
            if (cached2) this.rulesByMode.set(2, cached2);
            this.rulesMap = this.rulesByMode.get(this.validationMode) ?? cached2 ?? cached1 ?? {};
            this.bindFields();
            this.reapplyAll();
        }

        try {
            const [mode1, mode2] = await Promise.all([
                this.rulesService.getRules(this.validationEntity, 1),
                this.rulesService.getRules(this.validationEntity, 2),
            ]);
            this.rulesByMode.set(1, mode1);
            this.rulesByMode.set(2, mode2);
            this.rulesMap = this.rulesByMode.get(this.validationMode) ?? mode2 ?? {};
            console.log(`[validationEntity=${this.validationEntity}] loaded Mode1=${Object.keys(mode1).length} props, Mode2=${Object.keys(mode2).length} props, live=Mode${this.validationMode}`);
            this.bindFields();
            this.reapplyAll();
            if (this.liveValidation) {
                if (this.ngForm) {
                    this.subscription = this.ngForm.valueChanges?.pipe(debounceTime(50))
                        .subscribe(() => this.reapplyAll());
                } else {
                    this.host.nativeElement.addEventListener('input', this.domChangeHandler, true);
                    this.host.nativeElement.addEventListener('change', this.domChangeHandler, true);
                }
                this.zoneStableSub = this.zone.onStable.pipe(debounceTime(150))
                    .subscribe(() => this.handleZoneStable());
            }
            setTimeout(() => {
                this.bindFields();
                this.reapplyAll();
            }, 600);
        } catch (err) {
            console.error(`[validationEntity=${this.validationEntity}] failed to load rules`, err);
        }
    }

    private scheduleReapply(): void {
        if (this.reapplyTimer) clearTimeout(this.reapplyTimer);
        this.reapplyTimer = setTimeout(() => this.reapplyAll(), 100);
    }

    private handleZoneStable(): void {
        if (this.boundFields.length === 0) {
            this.rebindIfNewFields();
            return;
        }
        const hasDetached = this.boundFields.some(f => !f.el.isConnected);
        const candidatesNow = this.host.nativeElement.querySelectorAll(
            '[validationProperty], [validation-property], input[name], textarea[name], select[name]'
        ).length;
        if (hasDetached || candidatesNow !== this.lastCandidateCount) {
            this.boundFields.forEach(f => f.quillCleanup?.());
            this.boundFields = [];
            this.bindFields();
            this.lastZoneSnapshot = this.captureSnapshot();
            this.scheduleReapply();
            return;
        }
        const current = this.captureSnapshot();
        const hadPriorSnapshot = Object.keys(this.lastZoneSnapshot).length > 0;
        let changed = false;
        for (const key of Object.keys(current)) {
            if (current[key] !== this.lastZoneSnapshot[key]) {
                if (hadPriorSnapshot && this.lastZoneSnapshot[key] !== undefined) {
                    this.interactedFields.add(key);
                }
                changed = true;
            }
        }
        if (!changed) {
            for (const key of Object.keys(this.lastZoneSnapshot)) {
                if (current[key] !== this.lastZoneSnapshot[key]) {
                    if (hadPriorSnapshot) this.interactedFields.add(key);
                    changed = true;
                }
            }
        }
        if (changed) {
            this.lastZoneSnapshot = current;
            this.scheduleReapply();
        }
    }

    private rebindIfNewFields(): void {
        const candidatesNow = this.host.nativeElement.querySelectorAll(
            '[validationProperty], [validation-property], input[name], textarea[name], select[name]'
        ).length;
        if (candidatesNow > 0 && candidatesNow !== this.lastCandidateCount) {
            this.bindFields();
            this.lastZoneSnapshot = this.captureSnapshot();
            this.scheduleReapply();
        }
    }

    private captureSnapshot(): Record<string, string> {
        const snap: Record<string, string> = {};
        for (const f of this.boundFields) {
            const tag = f.el.tagName.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select') {
                snap[f.property] = (f.el as HTMLInputElement).value ?? '';
                continue;
            }
            const innerInput = f.el.querySelector('input, textarea, select') as HTMLInputElement | null;
            if (innerInput) {
                const dataValue = innerInput.getAttribute('data-value');
                snap[f.property] = (dataValue != null && dataValue.length > 0) ? dataValue : (innerInput.value ?? '');
            } else {
                snap[f.property] = '';
            }
        }
        return snap;
    }

    public switchMode(mode: number): void {
        this.validationMode = mode;
        this.rulesMap = this.rulesByMode.get(mode) ?? {};
        this.bindFields();
        this.reapplyAll();
    }

    public validateForSubmit(): boolean {
        const prev = this.validationMode;
        this.switchMode(1);
        const errors = this.errorsRegistry.snapshot(this.validationEntity);
        const hasErrors = Object.values(errors).some(arr => arr && arr.length > 0);
        if (!hasErrors) this.switchMode(prev);
        return !hasErrors;
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
        this.zoneStableSub?.unsubscribe();
        if (this.reapplyTimer) clearTimeout(this.reapplyTimer);
        this.host.nativeElement.removeEventListener('input', this.domChangeHandler, true);
        this.host.nativeElement.removeEventListener('change', this.domChangeHandler, true);
        this.boundFields.forEach(f => f.quillCleanup?.());
        this.boundFields = [];
        this.interactedFields.clear();
        if (this.validationEntity) this.errorsRegistry.clear(this.validationEntity);
    }

    private bindFields(): void {
        const lookup: Record<string, RuleSet[]> = {};
        for (const [key, rules] of Object.entries(this.rulesMap)) {
            lookup[key.toLowerCase()] = rules;
        }

        const candidates = this.host.nativeElement.querySelectorAll<HTMLElement>(
            '[validationProperty], [validation-property], input[name], textarea[name], select[name]'
        );
        this.lastCandidateCount = candidates.length;

        const hostEl = this.host.nativeElement;
        let bound = 0;
        candidates.forEach(el => {
            let p: HTMLElement | null = el.parentElement;
            while (p && p !== hostEl) {
                if (p.hasAttribute('validationEntity') || p.hasAttribute('validation-entity')) return;
                p = p.parentElement;
            }

            const property = el.getAttribute('validationProperty')
                ?? el.getAttribute('validation-property')
                ?? el.getAttribute('name');
            if (!property) return;
            const rules = lookup[property.toLowerCase()];
            if (!rules || rules.length === 0) return;

            this.boundFields.push({
                el,
                property,
                nameAttr: el.getAttribute('name'),
                isPEditor: el.tagName.toLowerCase() === 'p-editor',
                appliedAttrs: new Set<string>(),
            });
            bound++;
        });
        this.lastZoneSnapshot = this.captureSnapshot();
        console.log(`[validationEntity=${this.validationEntity}] bound ${bound} field(s)`);
    }

    private reapplyAll(): void {
        const dto = this.ngForm?.value ?? this.buildDtoFromDom();
        const conditions = buildConditions(dto);
        const lookup: Record<string, RuleSet[]> = {};
        for (const [key, rules] of Object.entries(this.rulesMap)) {
            lookup[key.toLowerCase()] = rules;
        }

        const errors: FieldErrors = {};

        for (const field of this.boundFields) {
            if (this.isFieldDisabled(field.el)) continue;

            const rules = lookup[field.property.toLowerCase()] ?? [];
            const activeRules = rules.filter(r =>
                !r.ConditionField || evaluateCondition(r.ConditionField, r.ConditionValue, conditions)
            );

            this.applyAttrs(field, activeRules);

            if (this.liveValidation) {
                const value = this.getValueFor(field, dto);
                const isEmpty = value == null || value === '' || (typeof value === 'string' && value.trim() === '');
                const isInteracted = this.interactedFields.has(field.property);
                const ctx: ClientValidationContext = { dto, conditions, customValidators: CUSTOM_VALIDATORS };
                const msgs: string[] = [];
                for (const rule of activeRules) {
                    const effective = (rule.Required === true && isEmpty && !isInteracted)
                        ? { ...rule, Required: false }
                        : rule;
                    const m = runRule(value, effective, field.property, ctx);
                    if (m && !msgs.includes(m)) msgs.push(m);
                }
                if (msgs.length > 0) errors[field.property] = msgs;
            }
        }

        if (this.liveValidation) this.errorsRegistry.setErrors(this.validationEntity, errors);
    }

    private applyAttrs(field: BoundField, activeRules: RuleSet[]): void {
        const merged: RuleSet = {};
        for (const r of activeRules) Object.assign(merged, r);

        const target: HTMLElement = field.isPEditor
            ? (field.el.querySelector('.ql-editor') as HTMLElement) ?? field.el
            : field.el;

        const set = (name: string, value: string | null) => {
            if (value == null) {
                if (field.appliedAttrs.has(name)) {
                    target.removeAttribute(name);
                    field.appliedAttrs.delete(name);
                }
            } else if (target.getAttribute(name) !== value) {
                target.setAttribute(name, value);
                field.appliedAttrs.add(name);
            }
        };

        set('maxlength', merged.MaxLength != null && !field.isPEditor ? String(merged.MaxLength) : null);
        set('minlength', merged.MinLength != null ? String(merged.MinLength) : null);
        set('min',       merged.Min != null ? String(merged.Min) : null);
        set('max',       merged.Max != null ? String(merged.Max) : null);
        set('required',  merged.Required === true ? '' : null);
        const pattern = merged.Regex ?? merged.Pattern;
        set('pattern',   pattern ? String(pattern) : null);

        // Retroactively truncate value if it exceeds newly-applied maxlength
        // (covers the case where user pasted text before the directive applied the attribute)
        if (merged.MaxLength != null && !field.isPEditor) {
            const max = Number(merged.MaxLength);
            const inputEl = (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
                ? target as HTMLInputElement | HTMLTextAreaElement
                : target.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null;
            if (inputEl && typeof inputEl.value === 'string' && inputEl.value.length > max) {
                inputEl.value = inputEl.value.substring(0, max);
                inputEl.dispatchEvent(new Event('input', { bubbles: true }));
                inputEl.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        if (field.isPEditor && merged.MaxLength != null && !field.quillCleanup) {
            this.bindPEditorMaxLength(field, Number(merged.MaxLength));
        }
    }

    private bindPEditorMaxLength(field: BoundField, maxLength: number, attempts = 0): void {
        const quill = this.findQuillInstance(field.el);
        if (!quill) {
            if (attempts > 25) return;
            setTimeout(() => this.bindPEditorMaxLength(field, maxLength, attempts + 1), 200);
            return;
        }
        const enforce = () => {
            const len = quill.getLength() - 1;
            if (len > maxLength) quill.deleteText(maxLength, len - maxLength, 'silent');
        };
        this.zone.runOutsideAngular(() => quill.on('text-change', enforce));
        field.quillCleanup = () => { try { quill.off('text-change', enforce); } catch {} };
    }

    private findQuillInstance(pEditor: HTMLElement): any {
        const tries: Array<() => any> = [
            () => (pEditor as any).quill,
            () => (window as any).ng?.getComponent?.(pEditor)?.quill,
            () => (pEditor.querySelector('.ql-container') as any)?.__quill ?? null,
        ];
        for (const fn of tries) {
            try {
                const q = fn();
                if (q && typeof q.getLength === 'function') return q;
            } catch {}
        }
        return null;
    }

    private isFieldDisabled(el: HTMLElement): boolean {
        const isDisabledNode = (n: HTMLElement): boolean =>
            n.hasAttribute('disabled') || (n as any).disabled === true;
        if (isDisabledNode(el)) return true;
        const inner = el.querySelector('input, textarea, select') as HTMLInputElement | null;
        if (inner && isDisabledNode(inner)) return true;
        let parent: HTMLElement | null = el.parentElement;
        const stopAt = this.host.nativeElement.parentElement;
        while (parent && parent !== stopAt) {
            const tag = parent.tagName.toLowerCase();
            if (tag === 'fieldset' && isDisabledNode(parent)) return true;
            parent = parent.parentElement;
        }
        return false;
    }

    private getValueFor(field: BoundField, dto: any): any {
        const lowerProp = field.property.charAt(0).toLowerCase() + field.property.slice(1);
        let modelValue: any = undefined;
        if (dto && Object.prototype.hasOwnProperty.call(dto, field.property)) modelValue = dto[field.property];
        else if (dto && Object.prototype.hasOwnProperty.call(dto, lowerProp)) modelValue = dto[lowerProp];
        else if (dto && field.nameAttr && Object.prototype.hasOwnProperty.call(dto, field.nameAttr)) modelValue = dto[field.nameAttr];

        if (modelValue == null && this.ngForm && field.nameAttr) {
            const control = this.ngForm.form?.get(field.nameAttr);
            if (control && control.value != null) modelValue = control.value;
        }

        if (field.isPEditor) {
            const ql = field.el.querySelector('.ql-editor') as HTMLElement;
            return ql ? ql.innerText.replace(/\n+$/, '') : '';
        }

        let domValue: any = undefined;
        const tag = field.el.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') {
            domValue = (field.el as HTMLInputElement).value;
        } else {
            const innerInput = field.el.querySelector('input, textarea, select') as HTMLInputElement | null;
            if (innerInput) {
                const dataValue = innerInput.getAttribute('data-value');
                domValue = (dataValue != null && dataValue.length > 0) ? dataValue : innerInput.value;
            }
        }

        const modelEmpty = modelValue == null || modelValue === '';
        const domHasMore = typeof domValue === 'string' && domValue.length > 0;
        if (modelEmpty && domHasMore) return domValue;
        return modelValue !== undefined ? modelValue : domValue;
    }

    private buildDtoFromDom(): any {
        const dto: any = {};
        for (const field of this.boundFields) {
            const value = this.getValueFor(field, null);
            const key = field.property.charAt(0).toLowerCase() + field.property.slice(1);
            dto[key] = value;
            dto[field.property] = value;
            if (field.nameAttr) dto[field.nameAttr] = value;
        }
        return dto;
    }
}
